'use es6';

import { combineReducers } from 'redux';
import { List, Map as ImmutableMap, Set as ImmutableSet, fromJS } from 'immutable';
import Actions from 'AdsUI/lib/Actions';
import { AudienceTypes, AudienceSources, SUPPORTED_AUDIENCE_TYPES, Networks } from 'AdsUI/lib/Constants';
import AudienceRecord from 'AdsUI/records/audiences/AudienceRecord';
import AudienceCountsRecord from 'AdsUI/records/audiences/AudienceCountsRecord';
import PixelRecord from 'AdsUI/records/audiences/PixelRecord';
import FBSavedAudienceRecord from 'AdsUI/records/audiences/FBSavedAudienceRecord';
import AudienceNetworkStateRecord from 'AdsUI/records/audiences/AudienceNetworkStateRecord';
import MembershipCountsRecord from 'AdsUI/records/audiences/MembershipCountsRecord';
import AdAccountIdentityRecord from 'AdsUI/records/ad-accounts/AdAccountIdentityRecord';
const AudienceTypesToAudienceSources = {
  [AudienceTypes.CONTACT_LIST]: AudienceSources.HUBSPOT_CONTACT_LIST,
  [AudienceTypes.WEBSITE]: AudienceSources.NETWORK_PIXEL,
  [AudienceTypes.LOOKALIKE]: AudienceSources.SEED_AUDIENCE,
  [AudienceTypes.COMPANY_LIST]: AudienceSources.HUBSPOT_COMPANY_LIST
};

const sortState = (state = ImmutableMap({
  column: 'name',
  direction: 'ascending'
}), action) => {
  switch (action.type) {
    case Actions.AUDIENCES_SORT:
      {
        const {
          column,
          direction
        } = action.payload;
        return new ImmutableMap({
          column,
          direction
        });
      }

    default:
      {
        return state;
      }
  }
};

const audiences = (state = ImmutableMap(), action) => {
  if (!action.payload) {
    return state;
  }

  switch (action.type) {
    case Actions.AUDIENCES_FETCH_SUCCEEDED:
      // TODO: mpape - remove after API switches
      return (action.payload.results || action.payload).reduce((map, audience) => {
        const audienceRecord = new AudienceRecord(audience);
        return map.set(audienceRecord.id, audienceRecord);
      }, ImmutableMap()).sortBy(audience => audience.getIn(['networkState', 'name']));

    case Actions.AUDIENCE_CREATE_SUCCEEDED:
      {
        const audience = new AudienceRecord(action.payload);
        return state.set(audience.id, audience);
      }

    case Actions.AUDIENCE_DELETE_SUCCEEDED:
      return state.delete(action.payload.audienceId);

    default:
      return state;
  }
};

const inactiveAudiences = (state = ImmutableMap(), action) => {
  if (!action.payload) {
    return state;
  }

  switch (action.type) {
    case Actions.AUDIENCES_INACTIVE_FETCH_SUCCEEDED:
      return (action.payload.results || action.payload).reduce((map, audience) => {
        const audienceRecord = new AudienceRecord(audience);
        return map.set(audienceRecord.id, audienceRecord);
      }, ImmutableMap()).sortBy(audience => audience.getIn(['networkState', 'name']));

    default:
      return state;
  }
};

const usageInfo = (state = new ImmutableMap({
  audienceCounts: {}
}), action) => {
  if (!action.payload) {
    return state;
  }

  switch (action.type) {
    case Actions.AUDIENCES_USAGE_INFO_FETCH_SUCCEEDED:
      {
        const {
          networkAudienceSourceCounts
        } = action.payload;
        return state.set('audienceCounts', new AudienceCountsRecord({
          networkAudienceSourceCounts: fromJS(networkAudienceSourceCounts)
        }));
      }

    case Actions.AUDIENCE_CREATE_SUCCEEDED:
      {
        if (action.payload.targetingAudience.audienceSource === AudienceTypes.ENGAGEMENT) {
          return state;
        }

        const audienceCounts = state.get('audienceCounts').updateIn(['networkAudienceSourceCounts', action.payload.targetingAudience.adNetwork, action.payload.targetingAudience.audienceSource], 0, cnt => cnt + 1);
        return state.set('audienceCounts', audienceCounts);
      }

    case Actions.AUDIENCE_DELETE_SUCCEEDED:
      {
        const audienceCounts = state.get('audienceCounts').updateIn(['networkAudienceSourceCounts', action.payload.adNetwork, action.payload.audienceSource], 0, cnt => cnt - 1);
        return state.set('audienceCounts', audienceCounts);
      }

    default:
      return state;
  }
};

const accountFilter = (state = ImmutableSet(), action) => {
  const {
    type,
    payload
  } = action;

  if (!payload) {
    return state;
  }

  switch (type) {
    case Actions.SELECTED_AD_ACCOUNTS_FETCH_SUCCEEDED:
    case Actions.AD_ACCOUNTS_OVERVIEW_FETCH_SUCCEEDED:
      {
        // earlyRequester (SELECTED_AD_ACCOUNTS_FETCH_SUCCEEDED) returns an array
        // fetchOverview (AD_ACCOUNTS_OVERVIEW_FETCH_SUCCEEDED) returns an object with an accounts key
        const accounts = Array.isArray(payload) ? payload : payload.accounts;
        return ImmutableSet(accounts.map(({
          accountId,
          network
        }) => new AdAccountIdentityRecord({
          accountId,
          network
        })));
      }

    case Actions.AUDIENCES_ACCOUNT_FILTER_CHANGE:
      return ImmutableSet(payload);

    default:
      return state;
  }
};

const pixels = (state = List(), action) => {
  const {
    type,
    payload
  } = action;

  if (!payload) {
    return state;
  }

  switch (type) {
    case Actions.PIXELS_FETCH_SUCCEEDED:
      // TODO: mpape - remove after API switches
      return List((payload.results || payload).map(pixel => new PixelRecord(pixel)));

    case Actions.PIXEL_INSTALL_SUCCEEDED:
      {
        const installedPixelIndex = state.findIndex(pixel => pixel.id === payload.pixel.id);
        return installedPixelIndex === -1 ? state : state.updateIn([installedPixelIndex], pixel => pixel.set('installed', true));
      }

    case Actions.PIXEL_CREATE_SUCCEEDED:
      return state.push(new PixelRecord(payload));

    default:
      return state;
  }
};

const supported = (state = ImmutableMap(), action) => {
  const {
    type,
    payload
  } = action;

  if (!payload) {
    return state;
  }

  switch (type) {
    case Actions.SUPPORTED_AUDIENCES_FETCH_SUCCEEDED:
      {
        const {
          adAccount
        } = payload;
        return state.setIn([adAccount.network, adAccount.accountId], SUPPORTED_AUDIENCE_TYPES.reduce((typeMap, audienceType) => typeMap.set(audienceType, List((payload[AudienceTypesToAudienceSources[audienceType]] || []).map(audience => new AudienceNetworkStateRecord(audience))).sortBy(audience => audience.name.toLowerCase())), state.get(adAccount, ImmutableMap())));
      }

    case Actions.SUPPORTED_AUDIENCES_UPDATE:
      {
        const {
          targetingAudience,
          networkState,
          type: audienceType
        } = payload;
        return state.updateIn([targetingAudience.adNetwork, targetingAudience.adAccountId, audienceType], existingAudiences => existingAudiences.push(new AudienceNetworkStateRecord(networkState)).sortBy(audience => audience.name.toLowerCase()));
      }

    case Actions.LINKEDIN_AUDIENCE_FACET_FETCH_SUCCEEDED:
      {
        const {
          adAccountId,
          audienceId,
          adTargetingFacetType
        } = payload;
        return state.updateIn([Networks.LINKEDIN, adAccountId, AudienceTypes.LOOKALIKE], existingAudiences => existingAudiences && existingAudiences.map(audience => audience.id === audienceId ? audience.set('linkedInAdTargetingFacet', adTargetingFacetType) : audience));
      }

    case Actions.SAVED_AUDIENCES_FETCH_SUCCEEDED:
      {
        const {
          adAccount,
          savedAudiences
        } = payload;
        return state.setIn([adAccount.network, adAccount.accountId, AudienceTypes.SAVED], List(savedAudiences.map(FBSavedAudienceRecord)).sortBy(audience => audience.name.toLowerCase()));
      }

    default:
      {
        return state;
      }
  }
};

const membershipCounts = (state = ImmutableMap(), action) => {
  const {
    type,
    payload = {}
  } = action;

  switch (type) {
    case Actions.MEMBERSHIP_COUNTS_FETCH_SUCCEEDED:
      {
        const {
          contactListId,
          membershipCounts: membershipCountsForList
        } = payload;
        return state.set(contactListId, new MembershipCountsRecord(membershipCountsForList));
      }

    default:
      {
        return state;
      }
  }
};

export default combineReducers({
  audiences,
  inactiveAudiences,
  usageInfo,
  accountFilter,
  pixels,
  sortState,
  supported,
  membershipCounts
});